/** React Query bindings over the read-only API client. */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchBalance,
  fetchCategories,
  fetchCategoryBreakdown,
  fetchDebt,
  fetchDebts,
  fetchMandatoryBurden,
  fetchMandatoryPayments,
  fetchNotes,
  fetchOperation,
  fetchOperations,
  fetchPlannedOperation,
  fetchPlannedOperations,
  fetchReadiness,
  fetchSession,
  fetchWish,
  fetchWishes,
  logIn,
  logOut,
} from './client';
import type {
  Category,
  DateRange,
  ListOperationsParams,
  MandatoryPaymentSort,
  NoteStatusFilter,
  OperationKind,
} from './types';

const READINESS_INTERVAL_MS = 30_000;

/** How often the list and the balance reload while auto-refresh is on. */
export const AUTO_REFRESH_INTERVAL_MS = 30_000;

/** Data changes outside this app (through MCP), so polling is opt-in. */
export type RefreshInterval = number | false;

export const SESSION_QUERY_KEY = 'session';

export const queryKeys = {
  notes: (status: NoteStatusFilter, limit: number, offset: number) =>
    ['notes', status, limit, offset] as const,
  operations: (params: ListOperationsParams) => ['operations', params] as const,
  operation: (id: string) => ['operation', id] as const,
  plannedOperations: (limit: number) => ['planned-operations', limit] as const,
  plannedOperation: (id: string) => ['planned-operation', id] as const,
  balance: () => ['balance'] as const,
  categoryBreakdown: (kind: OperationKind, range: DateRange) =>
    ['category-breakdown', kind, range] as const,
  categories: () => ['categories'] as const,
  wishes: (limit: number) => ['wishes', limit] as const,
  wish: (id: string) => ['wish', id] as const,
  debts: (limit: number) => ['debts', limit] as const,
  debt: (id: string) => ['debt', id] as const,
  mandatoryPayments: (limit: number, sort: MandatoryPaymentSort) =>
    ['mandatory-payments', limit, sort] as const,
  mandatoryBurden: () => ['mandatory-burden'] as const,
  readiness: () => ['readiness'] as const,
  session: () => [SESSION_QUERY_KEY] as const,
};

export function useOperations(params: ListOperationsParams, refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.operations(params),
    queryFn: () => fetchOperations(params),
    // A neighbouring page may stay visible while it loads. Changed filters
    // must not leave rows from another period or category on screen.
    placeholderData: (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey[1] as ListOperationsParams | undefined;
      return previousParams !== undefined && sameOperationFilters(previousParams, params)
        ? previousData
        : undefined;
    },
    refetchInterval,
  });
}

function sameOperationFilters(left: ListOperationsParams, right: ListOperationsParams): boolean {
  return (
    left.kind === right.kind &&
    left.category === right.category &&
    left.since === right.since &&
    left.until === right.until &&
    left.limit === right.limit
  );
}

export function useOperation(id: string | null) {
  return useQuery({
    queryKey: queryKeys.operation(id ?? ''),
    queryFn: () => fetchOperation(id as string),
    enabled: id !== null,
  });
}

export function useBalance(refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.balance(),
    queryFn: fetchBalance,
    refetchInterval,
  });
}

/** Covers the selected dates in one direction, not the page the table shows. */
export function useCategoryBreakdown(
  kind: OperationKind,
  range: DateRange,
  refetchInterval: RefreshInterval,
) {
  return useQuery({
    queryKey: queryKeys.categoryBreakdown(kind, range),
    queryFn: () => fetchCategoryBreakdown(kind, range),
    // Keep the chart on screen while the other direction loads, but never
    // present a breakdown from dates other than the inputs currently shown.
    placeholderData: (previousData, previousQuery) => {
      const previousRange = previousQuery?.queryKey[2] as DateRange | undefined;
      return previousRange?.since === range.since && previousRange.until === range.until
        ? previousData
        : undefined;
    },
    refetchInterval,
  });
}

/**
 * The list of categories, cached under one key: several components ask for it
 * to turn a name into a title, and they all read the same answer.
 *
 * It changes only when a category is added through MCP, so it is left to the
 * ordinary refresh rather than polled on its own.
 */
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: fetchCategories,
  });
}

/** What to call a category on screen: its title, or the bare name until it loads. */
export function useCategoryTitle(): (category: Category | null) => string | null {
  const { data } = useCategories();
  return (category) => {
    if (category === null) {
      return null;
    }
    return data?.find((known) => known.name === category)?.title ?? category;
  };
}

export function useWishes(limit: number, refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.wishes(limit),
    queryFn: () => fetchWishes(limit),
    refetchInterval,
  });
}

export function usePlannedOperations(limit: number, refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.plannedOperations(limit),
    queryFn: () => fetchPlannedOperations(limit),
    refetchInterval,
  });
}

/** Re-fetched by id so MCP edits are reflected when the modal opens. */
export function usePlannedOperation(id: string | null) {
  return useQuery({
    queryKey: queryKeys.plannedOperation(id ?? ''),
    queryFn: () => fetchPlannedOperation(id as string),
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Re-fetched by id so MCP edits are reflected when the drawer opens. */
export function useWish(id: string | null) {
  return useQuery({
    queryKey: queryKeys.wish(id ?? ''),
    queryFn: () => fetchWish(id as string),
    enabled: id !== null,
    // Opening the drawer is an explicit request to see the latest MCP changes.
    staleTime: 0,
  });
}

export function useDebts(limit: number, refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.debts(limit),
    queryFn: () => fetchDebts(limit),
    refetchInterval,
  });
}

/** Re-fetched by id, so a debt removed through MCP shows as gone. */
export function useDebt(id: string | null) {
  return useQuery({
    queryKey: queryKeys.debt(id ?? ''),
    queryFn: () => fetchDebt(id as string),
    enabled: id !== null,
  });
}

export function useMandatoryPayments(
  limit: number,
  sort: MandatoryPaymentSort,
  refetchInterval: RefreshInterval,
) {
  return useQuery({
    queryKey: queryKeys.mandatoryPayments(limit, sort),
    queryFn: () => fetchMandatoryPayments(limit, sort),
    placeholderData: keepPreviousData,
    refetchInterval,
  });
}

/** The total over every obligation, not only over the ones the panel shows. */
export function useMandatoryBurden(refetchInterval: RefreshInterval) {
  return useQuery({
    queryKey: queryKeys.mandatoryBurden(),
    queryFn: fetchMandatoryBurden,
    refetchInterval,
  });
}

/** The gate around everything else, so it is never served from a stale cache. */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: fetchSession,
    staleTime: 0,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logIn,
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session(), session);
      // Everything asked for while nobody was logged in came back with a 401.
      void queryClient.invalidateQueries();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logOut,
    onSuccess: (session) => {
      // Someone else may log in on this browser: leave them no cached balance.
      queryClient.removeQueries({
        predicate: ({ queryKey }) => queryKey[0] !== SESSION_QUERY_KEY,
      });
      queryClient.setQueryData(queryKeys.session(), session);
    },
  });
}

export function useReadiness() {
  return useQuery({
    queryKey: queryKeys.readiness(),
    queryFn: fetchReadiness,
    refetchInterval: READINESS_INTERVAL_MS,
  });
}

export function useNotes(
  status: NoteStatusFilter,
  limit: number,
  offset: number,
  refetchInterval: RefreshInterval,
) {
  return useQuery({
    queryKey: queryKeys.notes(status, limit, offset),
    queryFn: () => fetchNotes(status, limit, offset),
    refetchInterval,
  });
}
